import { tGiphyImage } from 'common/modules/ReactShared';
import {
  tDocumentRating,
  tDocumentStatus,
  RecordItem
} from 'common/modules/ReactShared/data/api/document/types';
import { tUserPaginationReturnValue } from '../../hooks/usePagination';

export enum AssignmentTaskType {
  Essay = 'Tasks::Essay',
  EssayAI = 'Tasks::EssayAI',
  Audio = 'Tasks::Audio',
  File = 'Tasks::File'
}

type tEssayType = 'Tasks::Essay';
type tEssayAIType = 'Tasks::EssayAI';
type tAudioType = 'Tasks::Audio';
type tFileType = 'Tasks::File';

export type tAssignmentType = tEssayType | tAudioType | tFileType | tEssayAIType;

export type tAssignmentInputType = 'text' | 'audio' | 'file';

export type tPersonInfo = {
  name: string | null;
  avatar: string | null;
  initials?: string | null;
};

type tAssignmentOwner = tPersonInfo;
export interface IChatMessageImage {
  imageURL: string | null;
  mobileImageURL: string | null;
  coverImg?: boolean;
  imageAlt: string | null;
}

export type tChatMessageAuthor = tPersonInfo;

export type tChatMessageFrom = 'incoming' | 'incoming:ai' | 'outgoing';

type tMediaContent =
  | {
      type: 'video';
      url: string;
    }
  | { type: 'audio'; url: string }
  | { type: 'image'; payload: IChatMessageImage }
  | { type: 'giphy'; payload: tGiphyImage }
  | null;

export type tMessageLinkPayload = {
  type: 'lessonId';
  data: number;
};
export interface IChatMessageFile {
  metadata: {
    filename: string;
    size: number;
    mime_type: string;
  };
  url?: string | null;
}

export interface IChatMessage {
  author: tChatMessageAuthor | null;
  editable?: boolean;
  createdAt: string | null;
  from: tChatMessageFrom;
  key: string;
  link?: {
    url: string | null;
    title: string | null;
    payload: tMessageLinkPayload | null;
  } | null;
  media?: tMediaContent;
  file?: IChatMessageFile | null;
  text?: string | null;
  title?: string | null;
  state: 'loading' | 'done';
}

interface IConversationAuthor {
  id: number;
  name: string;
  initials?: string | null;
  avatarURL: string | null;
  type: 'User' | 'Student';
}

type tConversationItemBase = {
  id: number;
  createdAt: string;
  content: string | null;
  audioURL: string | null;
  file?: {
    metadata: {
      filename: string;
      size: number;
      mime_type: string;
    };
    url: string | null;
  } | null;
};
type tConversationCommentItem = tConversationItemBase & {
  type: 'Comment';
  author: IConversationAuthor;
};
type tConversationAnswerItem = tConversationItemBase & {
  type: 'Document';
  author: null;
};

type tAssignmentConversationItem = tConversationCommentItem | tConversationAnswerItem;

type tConversation = tAssignmentConversationItem[];

export type tAssignmentsNormalizedListItem = {
  assignmentId: number;
  status: tDocumentStatus;
  createdAt: string;
  updatedAt: string;
  title: string;
  context: {
    imageURL: string | null;
    name: string | null;
    type: 'lesson' | 'school';
  } | null;
};
export type tAssignmentNormalizedData = {
  assignmentTask: {
    author: {
      brand?: string | null;
      name: string;
    };
    title: string;
    text: string;
    audioURL: string | null;
    videoURL: string | null;
    imageURL: string | null;
    imageAlt: string | null;
    mobileImageURL: string | null;
    giphyImage: tGiphyImage | null;
    coverImg: boolean;
    createdAt: string | null;
    answerType: tAssignmentInputType;
    minimalAnswerLength: number;
    lesson?: { id: number; title: string } | null;
    kind?: string;
    avgResponseTime?: string | null;
  };
  rating: tDocumentRating;
  estimatedReviewTime: string | null;
  status: tDocumentStatus;
  assignmentId: number | null;
  conversation: tConversation;
};

export type tUseAssignmentReturnValue = {
  title: string;
  inputType?: tAssignmentInputType | string;
  minimumAnswerLength: number;
  messages: IChatMessage[];
  status: tDocumentStatus;
  rating: tDocumentRating;
  estimatedReviewTime: string | null;
  assignmentId: number | null;
  owner: tAssignmentOwner;
  avgResponseTime: string | null;
};
export type tAudioSubmitType = string | Blob | FormData;
export type tAssignmentActions = {
  fetchAssignment: () => Promise<tAssignmentNormalizedData | null>;
  submitTextAnswer: (text: string) => Promise<void>;
  submitAudioAnswer: (
    audio: tAudioSubmitType
  ) => Promise<tAssignmentNormalizedData | null>;
  submitRate: (rating: tDocumentRating) => Promise<tAssignmentNormalizedData | null>;
};

type tLoadingStatus = 'fetch_loading' | 'submit_loading' | 'rate_loading';
export type tRequestStatus = 'initial' | tLoadingStatus | 'received' | 'submit_failed';

export type tCommonAssignmentReturnValue = {
  assignmentData: tUseAssignmentReturnValue | null;
  status: tRequestStatus;
  actions: tAssignmentActions;
  studentCanAddNewAnswer: boolean;
  lastStudentMessage: IChatMessage | null;
  canChangeLicense: boolean;
};

export type tAssignmentsTab =
  | 'pending_action'
  | 'waiting_for_review'
  | 'completed'
  | 'failed';

export type tCompletedSubTab = 'reviewed' | 'overdue';

export type tAssignmentsLoadParams = {
  searchValue?: string;
  page?: number;
  perPage?: number;
};

export type tTabCounts = {
  pending_action: number;
  waiting_for_review: number;
  completed: number;
  failed: number;
};

export type tUserAssignmentsReturnValue = {
  load: (tab?: tAssignmentsTab, params?: tAssignmentsLoadParams) => Promise<any>;
  changeTab: (tab: tAssignmentsTab, params?: tAssignmentsLoadParams) => Promise<void>;
  currentTab: tAssignmentsTab;
  completedSubTab: tCompletedSubTab;
  changeCompletedSubTab: (subTab: tCompletedSubTab) => void;
  status: tRequestStatus;
  data: RecordItem[] | null;
  failedData: RecordItem[] | null;
  failedPagination: tUserPaginationReturnValue;
  pagination: tUserPaginationReturnValue;
  isEmptyState: boolean;
  tabCounts: tTabCounts;
};
